// The paywall, between the start form and the first question (services/payments.ts on the API).
// The button opens Stripe Checkout; coupon codes are typed there. Through a discounted event link,
// the event's code is already on the checkout (nothing to type), and the page shows the event
// price. Stripe sends the person back to /take/:id?checkout=done&session_id=..., and TakeFlow asks
// the API to confirm it.

import { useEffect, useState } from "react";
import { api, type AppConfig } from "../lib/api";
import type { TakeView } from "../lib/types";
import { Page } from "./Page";

const money = (cents: number, currency = "usd") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase(), minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
const price = (cfg: AppConfig["paywall"]) => money(cfg?.priceCents ?? 2900, cfg?.currency);

/** Under the price: what happens next. The price itself is on the button (Edward, Nov 3: not "$29, paid once"). */
const AFTER_PAYING = "Once you've paid, you'll go straight into the test, and your results are yours to keep.";

/** The event price, the way Stripe works out the discount. */
function eventPrice(cfg: AppConfig["paywall"], d: { percentOff: number | null; amountOffCents: number | null }): string {
  const full = cfg?.priceCents ?? 2900;
  const cents = d.percentOff ? full - Math.round((full * d.percentOff) / 100) : Math.max(0, full - (d.amountOffCents ?? 0));
  return money(Math.max(0, cents), cfg?.currency);
}

/** Inside another site's frame (the embed) checkout can't open in place: it gets its own tab. */
const framed = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

export function Paywall({
  takeId,
  firstName,
  event = null,
  priceCents = null,
  codeAtCheckout = false,
  cancelled,
  onCheck,
}: {
  takeId: string;
  firstName: string;
  event?: TakeView["event"];
  /** This take's own price (a rater's offer), charged at checkout. */
  priceCents?: number | null;
  /** Checkout applies a code whose amount isn't known here (a personal link): no price on the button. */
  codeAtCheckout?: boolean;
  cancelled: boolean;
  onCheck: () => void;
}) {
  const [paywall, setPaywall] = useState<AppConfig["paywall"]>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openedInTab, setOpenedInTab] = useState(false);
  // Stripe wouldn't take the event's code: the checkout is full price, ready at this address.
  const [fullPriceUrl, setFullPriceUrl] = useState<string | null>(null);
  const discount = fullPriceUrl ? null : (event?.discount ?? null);

  useEffect(() => {
    api.getConfig().then((c) => setPaywall(c.paywall ?? null), () => undefined);
  }, []);

  const open = (url: string) => {
    if (framed()) {
      window.open(url, "_blank", "noopener");
      setOpenedInTab(true);
      setBusy(false);
    } else {
      window.location.assign(url);
    }
  };

  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      const { url, discount: applied, alreadyPaid } = await api.checkout(takeId);
      // An earlier checkout was paid after all (its return didn't land): straight to the questions.
      if (alreadyPaid || !url) {
        onCheck();
        return;
      }
      // We promised the event price and Stripe said no: say so before sending them to $29.
      if (applied === "unavailable" && event?.discount) {
        setFullPriceUrl(url);
        setBusy(false);
        return;
      }
      open(url);
    } catch {
      setError("Checkout isn't available right now. Please try again in a minute.");
      setBusy(false);
    }
  };

  return (
    <Page>
      <div className="mt-12 sm:mt-16">
        <p className="eyebrow text-accent-text">Before your questions</p>
        <h1 className="heading mt-2 text-balance text-[30px] leading-[1.1]">Thanks, {firstName}. One last step.</h1>
        {event ? (
          <p className="mt-4 text-pretty text-[17px] leading-relaxed" data-testid="event-line">
            You're here with <strong>{event.name}</strong>.
          </p>
        ) : null}
        {priceCents !== null ? (
          <>
            {/* A rater's offer: the price is already on the checkout, nothing to type. */}
            <div className="mt-6 rounded-[16px] border-2 border-accent bg-soft p-4" data-testid="offer-price">
              <p className="text-balance text-[17px] font-bold leading-snug">
                Your price is {money(priceCents, paywall?.currency)}, and it's already applied at checkout.
              </p>
              <p className="mt-1 text-pretty text-[15px] leading-relaxed">
                The Gifts Profile is usually {price(paywall)}, but you don't need to type anything. Your price is already on the checkout page. Got another code too? Just tap <strong>Add promotion code</strong> there.
              </p>
            </div>
            <p className="mt-4 text-[15px] leading-relaxed text-muted">{AFTER_PAYING}</p>
          </>
        ) : discount ? (
          <>
            {/* The event's code is already on the checkout: nothing to type, so no coupon box. */}
            <div className="mt-6 rounded-[16px] border-2 border-accent bg-soft p-4" data-testid="event-price">
              <p className="text-balance text-[17px] font-bold leading-snug">Your event price is {eventPrice(paywall, discount)}, and it's already applied at checkout.</p>
              <p className="mt-1 text-pretty text-[15px] leading-relaxed">
                The Gifts Profile is usually {price(paywall)}, but you don't need to type anything. Your event price is already on the checkout page.
              </p>
            </div>
            <p className="mt-4 text-[15px] leading-relaxed text-muted">{AFTER_PAYING}</p>
          </>
        ) : (
          <>
            {fullPriceUrl ? (
              <p role="status" className="mt-5 rounded-[16px] border border-line bg-panel p-4 text-pretty text-[15px] leading-relaxed">
                Sorry, your event price isn't available any more (it may have been used up or ended).
              </p>
            ) : null}
            <p className="mt-4 text-[17px] leading-relaxed text-muted">
              {AFTER_PAYING}
            </p>
            {/* The code note comes first, so nobody sees the price at checkout and thinks it's not what they signed up for. */}
            {codeAtCheckout ? (
              <div className="mt-6 rounded-[16px] border-2 border-accent bg-soft p-4" data-testid="code-applied">
                <p className="text-[17px] font-bold leading-snug">Your discount is already applied.</p>
                <p className="mt-1 text-pretty text-[15px] leading-relaxed">You don't need to type anything. You'll see your price on the checkout page.</p>
              </div>
            ) : (
              <div className="mt-6 rounded-[16px] border-2 border-accent bg-soft p-4" data-testid="code-note">
                <p className="text-[17px] font-bold leading-snug">Have a coupon code?</p>
                <p className="mt-1 text-pretty text-[15px] leading-relaxed">
                  You'll enter it on the next page. On the checkout screen, tap <strong>Add promotion code</strong> and the price comes off before you pay.
                </p>
              </div>
            )}
          </>
        )}
        {cancelled ? (
          <p role="status" className="mt-5 text-[15px]">
            Checkout was closed before paying. You can go back to it whenever you're ready.
          </p>
        ) : null}
        {openedInTab ? (
          <div role="status" className="mt-6 rounded-[16px] border border-line bg-panel p-4 text-[15px] leading-relaxed">
            Checkout opened in a new tab. Once you've paid there, come back here.
            <button type="button" className="btn btn-secondary mt-3 w-full" onClick={onCheck}>
              I've paid, continue
            </button>
          </div>
        ) : fullPriceUrl ? (
          <button type="button" className="btn btn-primary mt-6 w-full" onClick={() => open(fullPriceUrl)}>
            Take my Gifts Profile for {price(paywall)}
          </button>
        ) : (
          <button type="button" className="btn btn-primary mt-6 w-full" disabled={busy} onClick={go}>
            {busy
              ? "Opening checkout…"
              : priceCents === null && !discount && codeAtCheckout
                ? "Continue to checkout"
                : `Take my Gifts Profile for ${priceCents !== null ? money(priceCents, paywall?.currency) : discount ? eventPrice(paywall, discount) : price(paywall)}`}
          </button>
        )}
        {error ? (
          <p role="alert" className="mt-4 text-[15px] text-danger">
            {error}
          </p>
        ) : null}
        <p className="mt-6 text-[13px] text-muted">Payments are handled securely by Stripe.</p>
      </div>
    </Page>
  );
}
